<template>
  <SnowDataPoint
    :label-class-names="labelClassNames"
    :value-class-names="newSnowClassNames"
    :value-style="newSnowColor"
  >
    <font-awesome-icon
      v-if="!isAllAccess && isEstimated"
      icon="lock"
    />
    <span
      v-else
    >
      {{ newSnowFormatted }}<small
        v-if="isMetric"
        class="tw-text-xs"
      >cm</small>
    </span>
    <template #label>
      <span>{{ label }}</span>
      <span
        v-if="suspectReportAlertProperties"
        :class="suspectReportAlertProperties.class"
        :style="suspectReportAlertProperties.style"
        @click="handleShowDialog"
      />
      <Dialog
        :show-dialog="showDialog"
        @close="handleCloseDialog"
      >
        <Card
          class="tw-w-full lg:tw-w-96 tw-whitespace-normal"
          :has-body-shadow="true"
          :has-body-padding="true"
          :show-close-button="true"
          @close-button-click="handleCloseDialog"
        >
          <template #body>
            <h2
              v-if="suspectReportAlertProperties.label"
              class="tw-mb-4 section-title heading-color"
            >
              {{ suspectReportAlertProperties.label }}
            </h2>
            <p class="tw-text-base tw-mb-4">
              {{ suspectReportAlertProperties.description }}
            </p>
            <a
              v-if="suspectReportAlertProperties.linkUrl"
              class="tw-block tw-m-auto tw-w-max tw-text-base link-color-brand"
              :href="suspectReportAlertProperties.linkUrl"
              rel="noopener noreferrer"
              target="_blank"
            >
              {{ suspectReportAlertProperties.linkText }}
            </a>
          </template>
        </Card>
      </Dialog>
    </template>
  </SnowDataPoint>
</template>

<script>
import { mapState } from 'pinia';
import {
  dataNotAvailable,
  formatSnow,
  getAlertColorForeground,
  getLastItem,
  getLastPrecipSnow,
  getMaskImageStyle,
  isEstimated,
  isMetric,
} from '@@/utils/CommonUtils';
import { useMetaStore } from '@@/stores/Meta';
import { useUserStore } from '@@/stores/User';

export default {
  name: 'SnowNewSnowDataPoint',

  props: {
    label: {
      type: String,
      default: 'Last 24 Hours',
    },
    location: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      showDialog: false,
    };
  },

  computed: {
    ...mapState(useUserStore, { units: (state) => state.preferences.units }),

    ...mapState(useMetaStore, [
      'dataSourcesById',
      'locationTypesById',
      'getPowderAlert',
      'getSuspectReportAlert',
      'suspectReportAlert',
    ]),

    ...mapState(useUserStore, ['isAllAccess']),

    isEstimated() {
      return isEstimated(this.location, this.dataSourcesById);
    },

    isMetric() {
      return isMetric(this.units);
    },

    labelClassNames() {
      return `tw-whitespace-nowrap ${this.isEstimated ? 'estimated-border' : ''}`;
    },

    lastHistorySnowDaily() {
      return getLastItem(this.location.history_snow_daily);
    },

    newSnow() {
      return getLastPrecipSnow(this.location.history_snow_daily);
    },

    newSnowFormatted() {
      if (this.isMetric) {
        return this.newSnow;
      }

      return formatSnow(this.newSnow, this.units, true);
    },

    newSnowClassNames() {
      if (!this.isAllAccess && this.isEstimated) {
        return 'all-access-color';
      }

      if (this.newSnowFormatted === dataNotAvailable) {
        return 'text-light-color';
      }

      return null;
    },

    newSnowColor() {
      if (this.isAllAccess || !this.isEstimated) {
        const powderAlert = this.getPowderAlert(this.lastHistorySnowDaily?.alerts);
        const color = getAlertColorForeground(powderAlert);
        return { color };
      }

      return null;
    },

    newSnowLabelClassNames() {
      return this.isEstimated ? 'estimated-border' : null;
    },

    suspectReportAlertProperties() {
      const alert = this.getSuspectReportAlert(this.lastHistorySnowDaily?.alerts);

      if (!alert) {
        return null;
      }

      // Get the icon and color from the alert if present. Otherwise get them from the level.
      const iconUrl = alert.icon_url || alert.level.icon_url;
      const backgroundColor = alert.color_primary || alert.level.color_primary;

      return {
        class: 'tw-inline-block tw-w-4 tw-h-4 tw--mt-0.5 md:tw-ml-1 tw-align-middle tw-cursor-pointer',
        description: alert.description || alert.level.description,
        label: this.suspectReportAlert.label,
        linkText: alert.link_text,
        linkUrl: alert.link_url,
        style: {
          backgroundColor,
          ...getMaskImageStyle(iconUrl),
        },
      };
    },
  },

  methods: {
    handleShowDialog() {
      this.showDialog = true;
    },

    handleCloseDialog() {
      this.showDialog = false;
    },
  },
};
</script>
